import { captureException } from 'lib/reporting';
import { generateWebGLDebugInfo } from 'lib/utils/device';
import { CameraInspectionData, StreamStability } from 'verify/lib/interfaces/cameraInspection';
import { BLANK_CAPTURE_BYTE_SIZE } from 'verify/lib/utils/webcam';

/**
 * NB: This file is obsfuscated using jscrambler
 */

export const DEFAULT_CAMERA_INSPECTION_DATA: CameraInspectionData = {
  aspectRatio: 0,
  facingMode: '',
  frameRate: 0,
  height: 0,
  kind: '',
  label: '',
  streamStability: undefined,
  width: 0,
};

/**
 * We want to compare images across captures to determine stream stability.
 * However, storing entire images takes a lot of memory and can lead to crashes
 * or degraded behavior.
 * We instead take a quick-and-dirty hash of the image to save space.
 */
export function getStreamStabilitySnapshotHash(
  snapshotDataUrl: string | undefined,
): string | undefined {
  // Canvas context may not exist due to memory issues
  if (snapshotDataUrl == null) return;
  // filter out blank captures that results in false positives
  if (snapshotDataUrl.length <= BLANK_CAPTURE_BYTE_SIZE) return;

  // Because snapshotDataUri could be large, store a hash of the data URL to reduce memory consumption.
  const midpoint = Math.floor(snapshotDataUrl.length / 2);
  return (
    snapshotDataUrl.length.toString() +
    // - First 25 chars (minus metadata)
    snapshotDataUrl.slice(25, 25) +
    // - Middle 25 chars
    snapshotDataUrl.slice(midpoint, midpoint + 25) +
    // - Last 25 chars
    snapshotDataUrl.slice(-25)
  );
}

/**
 * Generates a partial of CameraInspectionData with the stream settings off the first video track
 */
export const generateStreamSettingsData = (
  stream: MediaStream,
):
  | Pick<CameraInspectionData, 'frameRate' | 'aspectRatio' | 'facingMode' | 'width' | 'height'>
  | Record<string, never> => {
  try {
    const streamSettings = stream.getVideoTracks()[0].getSettings();
    return {
      frameRate: streamSettings.frameRate,
      aspectRatio: streamSettings.aspectRatio,
      facingMode: streamSettings.facingMode,
      width: streamSettings.width,
      height: streamSettings.height,
    };
  } catch (err) {
    captureException(err, 'info', generateWebGLDebugInfo());
  }
  return {};
};

/**
 * Generate data based off `enumerateDevices`; picks the first video track to log its kind / label
 * and other metadata
 */
export const generateDeviceData = async (
  stream: MediaStream,
): Promise<
  | Pick<CameraInspectionData, 'kind' | 'label' | 'allCameraLabels' | 'selectedCameraIndex'>
  | Record<string, never>
> => {
  try {
    const mediaDevices = await navigator.mediaDevices?.enumerateDevices();

    // Use optional chaining as the stream may have killed itself if the user switched routes
    // before enumerateDevices was finished
    if (mediaDevices && stream?.getVideoTracks()?.length >= 1) {
      const videoDevices = mediaDevices.filter((device) => device.kind === 'videoinput');
      const streamSettings = stream?.getVideoTracks()[0].getSettings();
      const currentDeviceIndex = videoDevices.findIndex(
        (device) => device.deviceId === streamSettings.deviceId,
      );

      const currentDevice = currentDeviceIndex >= 0 ? videoDevices[currentDeviceIndex] : null;
      return {
        kind: currentDevice?.kind || '',
        label: currentDevice?.label || '',
        allCameraLabels: videoDevices.map((device) => device.label),
        selectedCameraIndex: currentDeviceIndex,
      };
    }
  } catch (err) {
    captureException(err, 'info', generateWebGLDebugInfo());
  }
  return {};
};

export const generateStreamStability = ({
  snapshots,
  includeSmallImages,
}: {
  snapshots: string[];
  includeSmallImages: boolean;
}) => {
  let duplicateSnapshotsPresent = false;

  /**
   * NB: This behaviour is duplicating a subtle bug introduced when we starting hashing
   * images to save memory using `getStreamStabilitySnapshotHash` -- that function ALREADY
   * filters out images that are greater than 10k, so now we're doing it twice unncessarily,
   * thus only leaving images that are extremely large.
   *
   * We cannot remove this behavior until we do an audit on id_electronic_replica_detection
   * to make sure it doesn't start triggering more false positives.
   */
  const validSnapshots = includeSmallImages
    ? snapshots
    : snapshots.filter((image) => image.length > 10000);

  if (snapshots.length > 1) {
    duplicateSnapshotsPresent = validSnapshots.some(
      (element, index) => snapshots.indexOf(element) !== index,
    );
  }

  return duplicateSnapshotsPresent ? StreamStability.Stable : StreamStability.Unstable;
};
